import WeddingGiftTile from './WeddingGiftTile'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import Group from 'components/utils/Group'
import PaginationDots from 'LuxKit/PaginationDots/PaginationDots'
import { useCallback, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import debounce from 'debounce-promise'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import type { WeddingGift } from 'weddings/api/weddings'
import { getLogicalScrollLeft } from 'LuxKit/Carousel/utils'

const GiftsCarousel = styled(CardCarousel)`
  width: 100%;
`

interface Props extends Partial<Omit<React.ComponentProps<typeof CardCarousel>, 'children'>> {
  gifts: Array<WeddingGift>
  renderGift?: (gift: WeddingGift) => React.ReactNode
  showPagination?: boolean
  onContributeClick?: (gift: WeddingGift) => void
}

function WeddingGiftsList(props: Props) {
  const { gifts, renderGift, showPagination = true, onContributeClick, ...carouselProps } = props

  const carouselRef = useRef<HTMLDivElement>(null)

  const [activeIndex, setActiveIndex] = useState(0)
  const pages = useMemo(() => {
    return Math.ceil(gifts.length / 8)
  }, [gifts])

  const debouncedOnScroll = useMemo(() => {
    return debounce(() => {
      if (carouselRef.current) {
        const newIndex = Math.round(getLogicalScrollLeft(carouselRef.current) / (carouselRef.current.scrollWidth / pages))
        setActiveIndex(Math.max(0, Math.min(newIndex, pages - 1)))
      }
    }, 50)
  }, [pages, carouselRef])

  const onScroll = useCallback(() => {
    debouncedOnScroll()
  }, [debouncedOnScroll])

  return (
    <>
      <GiftsCarousel
        desktopRows={gifts.length > 4 ? 2 : 1}
        desktopRowGap={28}
        gap={20}
        ref={carouselRef}
        onScroll={onScroll}
        {...carouselProps}
      >
        {gifts.map(gift => renderGift ? renderGift(gift) : (
          <WeddingGiftTile key={gift.id} gift={gift} onContributeClick={onContributeClick} />
        ))}
      </GiftsCarousel>
      {showPagination && (
        <CSSBreakpoint min="desktop">
          <Group direction="horizontal" gap={0} horizontalAlign="center">
            <PaginationDots
              count={pages}
              activeIndex={activeIndex}
            />
          </Group>
        </CSSBreakpoint>
      )}
    </>
  )
}

export default WeddingGiftsList
